import { debounce } from '@theme/utilities';

/**
 * Editable artwork details on the cart.
 *
 * ECC's portal keeps these editable on the cart so a buyer can revise an order
 * without starting over, and this mirrors that. The artwork file is the one
 * exception: Shopify accepts uploads only at add-to-cart, so it is displayed as
 * a link and preserved rather than edited.
 *
 * The important detail is that /cart/change.js REPLACES a line's properties
 * object wholesale. Sending only the field that changed would silently drop
 * every other property on that line - including the uploaded artwork. So each
 * update reads the current properties straight from the cart and merges.
 */
class YcOrderSummary extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onInput);
  }

  #onInput = (event) => {
    if (!event.target.matches('.yc-summary__meta-input')) return;
    this.#save(event.target);
  };

  #onChange = (event) => {
    if (!event.target.matches('.yc-summary__meta-input')) return;
    this.#save(event.target);
  };

  #save = debounce(async (input) => {
    const block = input.closest('.yc-summary__meta');
    const line = Number(block?.dataset.line);
    const key = input.dataset.property;
    if (!line || !key) return;

    const status = block.querySelector('.yc-summary__meta-status');
    input.setAttribute('aria-busy', 'true');
    this.#setStatus(status, 'Saving…');

    try {
      // Read current properties back from the cart rather than from the DOM, so
      // anything not rendered here (the artwork file, hidden _ properties) is
      // carried through untouched.
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      const current = cart.items[line - 1];
      if (!current) throw new Error('That line is no longer in your order.');

      const properties = { ...(current.properties || {}) };
      properties[key] = input.value;

      const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, properties }),
      });

      const raw = await response.text();
      let payload = null;
      try {
        payload = raw ? JSON.parse(raw) : null;
      } catch {
        payload = null;
      }
      if (!response.ok) {
        throw new Error(payload?.description || payload?.message || `Could not save (${response.status}).`);
      }

      this.#setStatus(status, 'Saved.');
    } catch (error) {
      this.#setStatus(status, error.message || 'Could not save.', true);
    } finally {
      input.removeAttribute('aria-busy');
    }
  }, 500);

  #setStatus(el, message, isError = false) {
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('yc-summary__meta-status--error', isError);
  }
}

if (!customElements.get('yc-order-summary-component')) {
  customElements.define('yc-order-summary-component', YcOrderSummary);
}
